import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { title: "", excerpt: "", cover_image: "", category: "Guides", author: "Mathura Homes", body: "", published: true };

export default function AdminBlogs() {
  const [blogs, setBlogs] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/blogs").then((r) => setBlogs(r.data)).catch(() => setBlogs([]));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    try {
      if (editing) await api.put(`/admin/blogs/${editing.id}`, form);
      else await api.post("/admin/blogs", form);
      toast.success(editing ? "Updated" : "Created"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (b) => { if (!window.confirm(`Delete "${b.title}"?`)) return; await api.delete(`/admin/blogs/${b.id}`); toast.success("Deleted"); load(); };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-3xl text-navy">Blogs</h1><p className="text-muted-foreground mt-1">Manage your articles.</p></div>
        <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="add-blog-btn" className="bg-gold text-navy hover:bg-gold-dark font-semibold"><Plus className="w-4 h-4 mr-1" /> Add Article</Button>
      </div>
      {blogs === null ? <Skeleton className="h-64 rounded-2xl" /> : (
        <div className="grid gap-4">
          {blogs.map((b) => (
            <div key={b.id} className="bg-white rounded-2xl border border-gray-100 shadow-card p-4 flex items-center gap-4" data-testid={`blog-admin-${b.id}`}>
              {b.cover_image ? <img src={b.cover_image} alt="" className="w-16 h-16 rounded-lg object-cover" /> : <div className="w-16 h-16 rounded-lg bg-muted" />}
              <div className="flex-1"><p className="font-semibold text-navy">{b.title}</p><p className="text-xs text-muted-foreground line-clamp-1">{b.excerpt}</p><span className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full ${b.published ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{b.published ? "Published" : "Draft"}</span></div>
              <Button size="icon" variant="ghost" onClick={() => { setEditing(b); setForm({ ...EMPTY, ...b }); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => del(b)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          {blogs.length === 0 && <p className="text-center text-muted-foreground py-10">No articles yet.</p>}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-navy">{editing ? "Edit Article" : "Add Article"}</DialogTitle></DialogHeader>
          <div><Label className="text-xs font-semibold text-navy">Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-gray-50 mt-1" data-testid="bf-title" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs font-semibold text-navy">Category</Label><Input value={form.category} onChange={(e) => set("category", e.target.value)} className="bg-gray-50 mt-1" /></div>
            <div><Label className="text-xs font-semibold text-navy">Author</Label><Input value={form.author} onChange={(e) => set("author", e.target.value)} className="bg-gray-50 mt-1" /></div>
          </div>
          <div><Label className="text-xs font-semibold text-navy">Cover Image URL</Label><Input value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} className="bg-gray-50 mt-1" /></div>
          <div><Label className="text-xs font-semibold text-navy">Excerpt</Label><Textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} className="bg-gray-50 mt-1" rows={2} /></div>
          <div><Label className="text-xs font-semibold text-navy">Body (HTML supported)</Label><Textarea value={form.body} onChange={(e) => set("body", e.target.value)} className="bg-gray-50 mt-1" rows={8} data-testid="bf-body" /></div>
          <div className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={(v) => set("published", v)} /><Label className="text-sm">Published</Label></div>
          <Button onClick={save} data-testid="save-blog-btn" className="bg-gold text-navy hover:bg-gold-dark font-semibold">Save Article</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
